'use client';
import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, ShieldCheck, RefreshCw, Mail, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

type Step = 'email' | 'otp' | 'password';

interface ForgotPasswordFlowProps {
  onBack: () => void;
}

export default function ForgotPasswordFlow({ onBack }: ForgotPasswordFlowProps) {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [otpVerified, setOtpVerified] = useState(false);

  const { register, handleSubmit, watch, formState: { errors } } = useForm<{ password: string; confirm: string }>();
  const passwordValue = watch('password', '');

  useEffect(() => {
    if (step === 'otp') {
      inputRefs.current[0]?.focus();
      setCountdown(60);
    }
  }, [step]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // Use Supabase signInWithOtp — exact same mechanism as login OTP
  async function sendSupabaseOtp(emailValue: string) {
    const supabase = createClient();
    const { error } = await supabase.auth.signInWithOtp({
      email: emailValue,
      options: { shouldCreateUser: false },
    });
    if (error) throw new Error(error.message);
  }

  async function handleSendOtp(emailValue: string) {
    setIsLoading(true);
    try {
      await sendSupabaseOtp(emailValue.trim().toLowerCase());
      setEmail(emailValue.trim().toLowerCase());
      setStep('otp');
      toast.success('An 8-digit code has been sent to your email.');
    } catch (err: any) {
      toast.error(err.message || 'Failed to send code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleResend() {
    setIsResending(true);
    try {
      await sendSupabaseOtp(email);
      setOtp(['', '', '', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      setCountdown(60);
      toast.success('A new code has been sent to your email.');
    } catch (err: any) {
      toast.error(err.message || 'Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  }

  function handleOtpChange(index: number, value: string) {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 7) inputRefs.current[index + 1]?.focus();
  }

  function handleOtpKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  function handleOtpPaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 8);
    if (!pasted) return;
    const newOtp = Array(8).fill('').map((_, i) => pasted[i] || '');
    setOtp(newOtp);
    const nextEmpty = newOtp.findIndex((v) => !v);
    inputRefs.current[nextEmpty === -1 ? 7 : nextEmpty]?.focus();
  }

  function handleVerifyOtp() {
    const code = otp.join('');
    if (code.length < 8) {
      toast.error('Please enter the full 8-digit code.');
      return;
    }
    setStep('password');
  }

  async function handleSetPassword(data: { password: string; confirm: string }) {
    const code = otp.join('');
    setIsLoading(true);
    try {
      const supabase = createClient();

      // If OTP hasn't been verified yet, verify it now to establish a session
      if (!otpVerified) {
        const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({
          email,
          token: code,
          type: 'email',
        });

        if (verifyError || !verifyData?.session) {
          throw new Error(verifyError?.message || 'Invalid or expired code. Please request a new one.');
        }
        setOtpVerified(true);
      }

      // Now update the password using the authenticated session
      const { error: updateError } = await supabase.auth.updateUser({
        password: data.password,
      });

      if (updateError) {
        throw new Error(updateError.message || 'Failed to update password. Please try again.');
      }

      // Sign out after password reset so user logs in fresh
      await supabase.auth.signOut();

      toast.success('Password reset successfully! You can now log in.');
      onBack();
    } catch (err: any) {
      toast.error(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  // Step 1: Email
  if (step === 'email') {
    return (
      <div>
        <div className="mb-6">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Mail size={24} className="text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-1">Forgot password?</h1>
          <p className="text-sm text-muted-foreground">
            Enter your email and we&apos;ll send you a 6-digit reset code.
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const emailInput = form.querySelector<HTMLInputElement>('#fp-email');
            if (emailInput?.value) handleSendOtp(emailInput.value.trim());
          }}
          className="space-y-4"
          noValidate
        >
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="fp-email">
              Email address
            </label>
            <input
              id="fp-email"
              type="email"
              placeholder="you@example.com"
              required
              className="w-full px-4 py-3 bg-card border border-input rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:bg-cyan-600 active:scale-95 transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed"
            style={{ minHeight: '48px' }}
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Sending code...
              </>
            ) : (
              'Send Reset Code'
            )}
          </button>
        </form>

        <button
          type="button"
          onClick={onBack}
          className="mt-4 w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Back to login
        </button>
      </div>
    );
  }

  // Step 2: OTP
  if (step === 'otp') {
    return (
      <div className="flex flex-col items-center text-center py-2">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <ShieldCheck size={28} className="text-primary" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">Enter reset code</h1>
        <p className="text-sm text-muted-foreground mb-1">We sent an 8-digit code to</p>
        <p className="text-sm font-semibold text-foreground mb-6">{email}</p>

        <div className="flex gap-2 mb-6" onPaste={handleOtpPaste}>
          {otp.map((digit, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(i, e.target.value)}
              onKeyDown={(e) => handleOtpKeyDown(i, e)}
              className="w-11 h-14 text-center text-xl font-bold bg-card border-2 rounded-xl text-foreground outline-none transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/20 border-input"
              aria-label={`OTP digit ${i + 1}`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={handleVerifyOtp}
          disabled={otp.join('').length < 8}
          className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:bg-cyan-600 active:scale-95 transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed mb-4"
          style={{ minHeight: '48px' }}
        >
          Verify Code
        </button>

        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
          {countdown > 0 ? (
            <span>Resend code in <span className="font-semibold text-foreground">{countdown}s</span></span>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              disabled={isResending}
              className="flex items-center gap-1.5 text-primary font-semibold hover:underline disabled:opacity-60"
            >
              {isResending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Resend code
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={() => setStep('email')}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Back
        </button>
      </div>
    );
  }

  // Step 3: New Password
  return (
    <div>
      <div className="mb-6">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <KeyRound size={24} className="text-primary" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-1">Set new password</h1>
        <p className="text-sm text-muted-foreground">Choose a strong password for your account.</p>
      </div>

      <form onSubmit={handleSubmit(handleSetPassword)} className="space-y-4" noValidate>
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="fp-new-password">
            New password
          </label>
          <div className="relative">
            <input
              id="fp-new-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="At least 8 characters"
              className={`w-full px-4 py-3 pr-11 bg-card border rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/20 ${errors.password ? 'border-danger' : 'border-input'}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs text-danger font-medium">{errors.password.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="fp-confirm-password">
            Confirm password
          </label>
          <div className="relative">
            <input
              id="fp-confirm-password"
              type={showConfirm ? 'text' : 'password'}
              placeholder="Re-enter your password"
              className={`w-full px-4 py-3 pr-11 bg-card border rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/20 ${errors.confirm ? 'border-danger' : 'border-input'}`}
              {...register('confirm', {
                required: 'Please confirm your password',
                validate: (v) => v === passwordValue || 'Passwords do not match',
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirm && <p className="mt-1 text-xs text-danger font-medium">{errors.confirm.message}</p>}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:bg-cyan-600 active:scale-95 transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed"
          style={{ minHeight: '48px' }}
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Resetting password...
            </>
          ) : (
            'Reset Password'
          )}
        </button>
      </form>

      <button
        type="button"
        onClick={() => setStep('otp')}
        className="mt-4 w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        ← Back
      </button>
    </div>
  );
}
